import React, { useState } from 'react';
import axios from 'axios';
import { useRouter } from 'next/router';
import StarRating from './StarRating';
import Link from 'next/link';

interface Review {
  id: string;
  rating: number;
  author: string;
  text: string;
}

interface Mechanic {
  id: string;
  business_name: string;
  address: string;
  city?: string;
  state?: string;
  zip?: string;
  phone: string;
  email?: string;
  website?: string;
  latitude?: number;
  longitude?: number;
  avg_rating: number;
  review_count: number;
  tag_makes?: string[];
  tag_services?: string[];
  business_hours?: string;
  reviews?: Review[];
}

interface MechanicCardProps {
  mechanic: Mechanic;
  isSelected: boolean;
  onClick: () => void;
}

interface MechanicsListProps {
  mechanics: Mechanic[];
  total: number;
  limit: number;
  offset: number;
  onPageChange: (newOffset: number) => void;
  shouldRenderPagination?: boolean;
}

function MechanicCard({ mechanic, isSelected, onClick }: MechanicCardProps) {
  const router = useRouter();
  const [isNavigating, setIsNavigating] = useState(false);

  // Ensure rating and review count are valid numbers
  const rating = typeof mechanic.avg_rating === 'string' ? parseFloat(mechanic.avg_rating) : mechanic.avg_rating;
  const reviewCount = typeof mechanic.review_count === 'string' ? parseInt(mechanic.review_count) : mechanic.review_count;

  const handleCardClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!mechanic.id) {
      console.error('Missing mechanic ID, cannot navigate');
      return;
    }
    
    // Log the actual ID for debugging
    console.log(`Direct navigation to mechanic details, ID:`, mechanic.id);
    
    try {
      // Use router.push for better navigation
      router.push({
        pathname: '/mechanics/[id]',
        query: { id: mechanic.id }
      });
    } catch (error) {
      console.error('Navigation failed, using fallback method', error);
      // Fallback to direct URL construction if router fails
      const encodedId = encodeURIComponent(String(mechanic.id));
      window.location.href = `/mechanics/${encodedId}`;
    }
  };

  return (
    <div 
      className={`block bg-white rounded-lg shadow-md p-6 cursor-pointer transition-all hover:shadow-lg ${
        isSelected ? 'ring-2 ring-blue-500' : ''
      }`}
      onClick={handleCardClick}
    >
      <div className="flex items-start">
        {/* Icon */}
        <div className="p-3 bg-blue-100 rounded-lg">
          <svg
            className="w-6 h-6 text-blue-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
        </div>

        {/* Content */}
        <div className="ml-4 flex-1">
          <h3 className="text-lg font-bold text-gray-900 hover:text-blue-600">
            {mechanic.business_name}
          </h3>
          <p className="text-gray-600">{mechanic.address}</p>
          {mechanic.phone && (
            <p className="text-gray-600">
              <a 
                href={`tel:${mechanic.phone}`} 
                className="text-blue-600 hover:text-blue-800"
                onClick={(e) => e.stopPropagation()} // Don't trigger card click when clicking phone
              >
                {mechanic.phone}
              </a>
            </p>
          )}
          {/* Rating */}
          <div className="mt-2">
            <StarRating 
              rating={rating}
              showCount 
              count={reviewCount}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function MechanicsList({ mechanics, total, limit, offset, onPageChange, shouldRenderPagination = true }: MechanicsListProps) {
  const [selectedMechanic, setSelectedMechanic] = useState<Mechanic | null>(null);
  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;
  const router = useRouter();
  
  // Log info for debugging
  console.log('MechanicsList props received:', {
    mechanicsCount: mechanics?.length || 0,
    total,
    limit,
    offset, 
    mechanicsIsArray: Array.isArray(mechanics),
    firstMechanic: mechanics?.length > 0 ? mechanics[0] : null
  });

  const handlePageClick = (page: number) => {
    onPageChange((page - 1) * limit);
  };

  if (!mechanics || !Array.isArray(mechanics)) {
    console.error('Invalid mechanics data:', mechanics);
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">Error loading mechanics data</h3>
        <p className="mt-2 text-gray-500">The data received was in an invalid format</p>
      </div>
    );
  }
  
  if (mechanics.length === 0) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">No mechanics found</h3>
        <p className="mt-2 text-gray-500">Try adjusting your search or filters</p>
      </div>
    );
  }

  return (
    <div>
      <div className="space-y-4 mb-6">
        {mechanics.map((mechanic) => (
          <MechanicCard
            key={`mechanic-${mechanic.id}`}
            mechanic={mechanic}
            isSelected={selectedMechanic?.id === mechanic.id}
            onClick={() => setSelectedMechanic(mechanic)}
          />
        ))}
      </div>
      
      {shouldRenderPagination && totalPages > 1 && (
        <div className="flex justify-center space-x-1 overflow-x-auto py-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={`page-${i + 1}`}
              onClick={() => handlePageClick(i + 1)}
              className={`px-2 py-1 text-sm rounded transition min-w-[32px] min-h-[32px] font-medium focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 whitespace-nowrap
                ${currentPage === i + 1
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}
              `}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default MechanicsList; 